import { useSearch } from "@tanstack/react-router";
import { useGetRolesQuery } from "api/TenantSlug/Role/hooks";
import { useExportUsersMutation, useGetUsersQuery } from "api/TenantSlug/User/hooks.ts";
import { AddAdvertiserDrawer } from "components/Drawers/AddAdvertiserDrawer/AddAdvertiserDrawer";
import { AdvertiserDetailDrawer } from "components/Drawers/AdvertiserDetailDrawer/AdvertiserDetailDrawer";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom.ts";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { LabelsCell } from "components/UI/Table/UI/LabelsCell/LabelsCell.tsx";
import { useDrawer } from "hooks/useDrawer";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { useUser } from "hooks/useUser";
import { useRecoilValue } from "recoil";
import { PermissionEnum, RoleEnum } from "types/enums";
import { DrawerId, TableId } from "types/models.ts";

import RoleData = App.Data.RoleData;
import UserData = App.Data.UserData;
import GetUsersResponse = App.Http.Responses.User.GetUsersResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

interface SearchParams {
    [`advertiserDetail-advertiserId`]: string;
}

export const AdvertisersTable = () => {
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const userUtils = useUser();
    const permissionUtils = usePermission();
    const activeTenant = userUtils.getActiveTenant();
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const tableId = TableId.AdvertisersTable;
    const tableAtomInstance = tableAtom<UserData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const drawerUtils = useDrawer();
    const getRolesQuery = useGetRolesQuery({});
    const advertiserRole = getRolesQuery.data?.roles.find((role) => role.name === RoleEnum.Advertiser);
    const exportState = useRecoilValue(exportAtom(tableId));
    const canViewLabels = permissionUtils.hasPermission(PermissionEnum.UserLabelsView);

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                ...(tableState.filter?.filter ?? {}),
                ...{
                    "roles.id": advertiserRole?.id ?? "",
                },
            },
            sort: tableState.filter?.sort ?? "nickname",
            include: "labels",
        },
    };

    const query = useGetUsersQuery(request, {
        enabled: !!advertiserRole?.id,
    });

    const exportUsersMutation = useExportUsersMutation();
    const tableFetchDataUtils = useTableDataApiOperations<
        GetUsersResponse,
        void,
        Record<string, never>,
        BaseExportRequest
    >(tableId, query, undefined, undefined, exportUsersMutation, {
        additionalDependencies: [advertiserRole?.id],
        shouldFetch: !!advertiserRole?.id,
    });

    const columnConfig: TableColumnType<UserData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "nickname",
            key: "nickname",
            render: (record) => {
                return record;
            },
            sorter: true,
            defaultSorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            ellipsis: true,
            width: 150,
        },
        ...(canViewLabels
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("tags")} />,
                      dataIndex: "labels.name",
                      key: "labels.name",
                      render: (_: unknown, data: UserData) => <LabelsCell labels={data.labels} />,
                      filterable: true,
                      filterType: FILTER_TYPES.labelMultiSelect,
                      ellipsis: false,
                      width: 150,
                  },
              ]
            : []),
        /*  {
            title: <ColumnHeaderTitle columnTitle="Země" />,
            dataIndex: "zeme",
            key: "zeme",
            render: () => {
                return (
                    <>
                        <Flag code={"CZ"} className={"h-[13px] rounded-[3px]"} />
                    </>
                );
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Jazyk" />,
            dataIndex: "jazyk",
            key: "jazyk",
            render: () => {
                return (
                    <>
                        <Flag code={"CZ"} className={"h-[13px] rounded-[3px]"} />
                    </>
                );
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Skupina" />,
            dataIndex: "user_commissionGroups",
            key: "user_commissionGroups",
            render: () => {
                const groupOptions = [
                    { label: "Základní", value: "default", key: "default" },
                    ...(fetchCommissionListQuery.data?.commissionGroups?.map((group) => ({
                        label: group.title,
                        value: group.id,
                        key: group.id,
                    })) || []),
                ];

                return (
                    <span onClick={(e) => e.stopPropagation()}>
                        <Select options={groupOptions} className="w-full" defaultValue={"default"} />
                    </span>
                );
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Kategorie" />,
            dataIndex: "kategorie",
            key: "kategorie",
            render: () => {
                const groupOptions = [{ label: "Základní", value: "default", key: "default" }];

                return (
                    <span onClick={(e) => e.stopPropagation()}>
                        <Select options={groupOptions} className="w-full" />
                    </span>
                );
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Štítky" />,
            key: "stitky",
            dataIndex: "stitky",
            render: () => (
                <Flex gap={"gap-x-2"} align="center">
                    <Tag closable closeIcon={<CloseOutlined />}>
                        TOP
                    </Tag>
                    <Tag closable closeIcon={<CloseOutlined />}>
                        Česko
                    </Tag>
                </Flex>
            ),
        },
        {
            title: <ColumnHeaderTitle columnTitle="Manažer" />,
            dataIndex: "manazer",
            key: "manazer",
            render: () => {
                const groupOptions = [{ label: "Základní", value: "default", key: "default" }];

                return (
                    <span onClick={(e) => e.stopPropagation()}>
                        <Select options={groupOptions} className="w-full" />
                    </span>
                );
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Stav" />,
            key: "stav",
            dataIndex: "stav",
            render: () => {
                const color = "green";
                return (
                    <Space>
                        <span style={{ color: color, fontSize: "24px" }}>•</span>
                        Povoleno
                    </Space>
                );
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Schválení" />,
            key: "schvaleni",
            dataIndex: "schvaleni",
            render: (_, _data, index) => {
                return (
                    <AcceptRejectTableButtons
                        isAccepted={index % 2 === 0}
                        isRejected={index % 2 !== 0}
                        onReject={() => console.log("rejected")}
                        onAccept={() => console.log("accepted")}
                    />
                );
            },
        },*/
    ];

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<UserData>({
        entityName: "advertiser",
        editPermission: PermissionEnum.AdvertiserEntryEdit,
        detailPermission: PermissionEnum.AdvertiserEntryView,
        detailDrawerId: DrawerId.AdvertiserDetail,
        detailDrawerParam: `${DrawerId.AdvertiserDetail}-advertiserId`,
    });

    return (
        <>
            {searchParams[`${DrawerId.AdvertiserDetail}-advertiserId`] && (
                <AdvertiserDetailDrawer advertiserId={searchParams[`${DrawerId.AdvertiserDetail}-advertiserId`]} />
            )}
            {activeTenant?.userRoles?.some((role: RoleData) =>
                [RoleEnum.AdminFree, RoleEnum.AdminBeast].includes(role.name)
            ) && (
                <AddAdvertiserDrawer
                    onSuccess={() => {
                        drawerUtils.hide(DrawerId.AddAdvertiser);
                        tableFetchDataUtils.refetchData();
                    }}
                />
            )}
            <Table<GetUsersResponse, UserData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetUsersResponse}
                dataSource={tableFetchDataUtils.tableData?.users || []}
                columnConfig={columnConfig}
                loading={query.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                rowKey={(record) => record.id}
                addEntityData={{
                    drawerId: DrawerId.AddAdvertiser,
                }}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                autoScrollWidth={true}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
                hideActionsColumn={true}
                enableFilters={true}
                searchDataIndex="nickname"
            />
        </>
    );
};
